import { StructuredBriefFile } from '__generated__/StructuredBriefFile';
import {
    NationBuilderAppType,
    NationBuilderMobileAppType,
    NationBuilderNeeds,
} from '__generated__/types';
import { Uploader } from '__generated__/Uploader';
import Text from 'common/components/Text';
import BoxSelectField from 'projects/components/BriefFields/BoxSelectField';
import DescriptionField from 'projects/components/BriefFields/DescriptionField';
import InspirationFilesField from 'projects/components/BriefFields/InspirationFilesField';
import InspirationUrlsField from 'projects/components/BriefFields/InspirationUrlsField';
import LanguageField from 'projects/components/BriefFields/LanguageField';
import ReferenceFilesField from 'projects/components/BriefFields/ReferenceFilesField';
import TitleField from 'projects/components/BriefFields/TitleField';
import ContentSetting from 'projects/components/ContentSetting';
import { ObservableField, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import React, { useEffect } from 'react';
import { nationBuilderAppType, nationBuilderMobileAppType, nationBuilderNeeds } from './enums';

interface Fields {
    language: string;
    title: string;
    needs: NationBuilderNeeds;
    appType: NationBuilderAppType;
    mobileAppType: NationBuilderMobileAppType;
    existingWebsite: string;
    description: string;
    referenceFiles: StructuredBriefFile[];
    inspirationFiles: StructuredBriefFile[];
    inspirationUrls: string[];
}

export interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
        uploader: Uploader;
    };
}

export default function DetailsPage<T extends Fields>(props: Props<T>) {
    const { form, brief } = props;

    useEffect(() => {
        trackViewedBriefEvent({ id: brief.id, page: 'Details' });
    }, []);

    return (
        <ContentSetting>
            <LanguageField form={form} />
            <TitleField
                form={form}
                placeholderText={__(
                    'E.g. Website for an organization that works towards protecting the Earth from asteroid impacts and issues of planetary defense.'
                )}
            />
            <BoxSelectField
                form={form}
                name="needs"
                title={__('What best describes your need?')}
                options={nationBuilderNeeds}
                required
            />
            {form.get('needs') === NationBuilderNeeds.APP_OR_INTEGRATION && (
                <BoxSelectField
                    form={form}
                    name="appType"
                    title={__('Do you need need help with a web or mobile application?')}
                    options={nationBuilderAppType}
                    required
                />
            )}
            {form.get('needs') === NationBuilderNeeds.APP_OR_INTEGRATION &&
                form.get('appType') === NationBuilderAppType.MOBILE && (
                    <BoxSelectField
                        form={form}
                        name="mobileAppType"
                        title={__('Do you need help with iOS, Android or Both?')}
                        options={nationBuilderMobileAppType}
                        required
                    />
                )}
            {form.get('needs') && form.get('needs') !== NationBuilderNeeds.NEW_SITE && (
                <ObservableField
                    form={form}
                    name="existingWebsite"
                    title={__('Link to existing website')}
                    required
                    render={() => (
                        <Text
                            placeholder={__('www.example.com')}
                            fill={true}
                            {...form.attach('existingWebsite')}
                        />
                    )}
                />
            )}
            <DescriptionField
                form={form}
                placeholderText={__(
                    'E.g. We provide a non-governmental voice on the risks, options, and implications of asteroid data while advancing the technical means by which that data is acquired. We work to make interpretation of asteroid data open and accessible, and we serve as an informed source for an international community of policy makers and scientists who can best help to achieve these goals.'
                )}
            />
            <ReferenceFilesField form={form} uploader={brief.uploader} />
            <InspirationFilesField form={form} uploader={brief.uploader} />
            <InspirationUrlsField form={form} />
        </ContentSetting>
    );
}
